import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Close from '@mui/icons-material/Close';
import PersonPinCircleSharpIcon from '@mui/icons-material/PersonPinCircleSharp';
import {
  TextField,
  List,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
} from '@mui/material';

import notifyBugsnag from '../../../util/notifyBugsnag';
import { googleMapsErrorState } from '../../../redux/legacy/reducers/ducks/legacy/MainDuck';
import useClasses from '../../../hooks/legacy/useClasses';
import { setOrderAddress } from '../../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import { useWhere } from '../../../hooks/useWhere';
import useGooglePlaces from '../../../hooks/useGooglePlaces';

import styles from './GoogleAutoCompleteInput.style';

const debounceSearchMinLength = 2;
const autocompleteMaxResults = 10;

export default function GoogleAutoCompleteInput({
  showIcon,
  streetAddress,
  setStreetAddress,
  disabled,
  onAfterSelect,
  helperText,
}) {
  const dispatch = useDispatch();
  const classes = useClasses(styles);
  const { setSelectedAddress } = useWhere();
  const [onSearch, setOnSearch] = useState(true);

  const {
    placePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading,
    fetchPlaceDetails,
  } = useGooglePlaces({
    regionCodes: ['us'],
  });

  const [addressString, setAddressString] = useState('');

  const { address, googleMapsErrorStates } = useSelector(
    ({ address: selectedAddress, main, profile }) => ({
      address: selectedAddress.selectedAddress || null,
      googleMapsErrorStates: main.googleMapsErrorState,
      addresses: profile.getAddressesResponse?.response,
    })
  );

  useEffect(() => {
    if (address) {
      setAddressString(
        showIcon ? address?.address || '' : address?.entered_address || ''
      );
      dispatch(googleMapsErrorState(false));
    }
  }, [address, showIcon]);

  const onInputChange = (evt) => {
    if (!onSearch) {
      setOnSearch(true);
    }

    if (!evt.target.value && Object.keys(streetAddress).length > 0) {
      setStreetAddress({ ...streetAddress, route: '' });
    }
    setAddressString(evt.target.value);

    if (evt.target.value.length > debounceSearchMinLength) {
      getPlacePredictions({ input: evt.target.value });
    }
  };

  const onPlaceSelected = async (place) => {
    setOnSearch(false);

    try {
      const details = await fetchPlaceDetails(place.place_id);
      if (!details) return;

      const { formattedAddress, addressComponents, location } = details;

      if (!location) return;

      const getAddressItem = (key) =>
        addressComponents?.find((c) => c.types.includes(key))?.shortText;

      const route = getAddressItem('route');
      const number = getAddressItem('street_number');

      // Prefer sublocality (village/neighbourhood) over the legal municipality
      // (locality) so addresses like "Ivoryton" are not silently changed to
      // "Essex" by Google's normalisation.
      const city =
        getAddressItem('sublocality_level_1') ||
        getAddressItem('sublocality') ||
        getAddressItem('neighborhood') ||
        getAddressItem('locality') ||
        getAddressItem('administrative_area_level_2');

      const state = getAddressItem('administrative_area_level_1');
      const zip = getAddressItem('postal_code');

      const lat = location.lat();
      const lng = location.lng();

      const resolvedStreetAddress = `${number ?? ''} ${route ?? ''}`.trim();

      // Rebuild display address from resolved components so entered_address
      // matches the saved city field. Format: "<street>, <city>, <state> <zip>"
      const stateZip = [state, zip].filter(Boolean).join(' ');
      const resolvedFullAddress = [
        resolvedStreetAddress || route || '',
        city,
        stateZip,
      ]
        .filter(Boolean)
        .join(', ');
      const displayAddress = resolvedFullAddress || formattedAddress;

      const selectedAddress = {
        entered_address: displayAddress,
        address: resolvedStreetAddress || formattedAddress,
        city,
        state,
        zipcode: zip,
        latitude: lat,
        longitude: lng,
        placeId: place.place_id,
      };
      setSelectedAddress(selectedAddress);

      setAddressString(
        showIcon ? resolvedStreetAddress || formattedAddress : displayAddress
      );

      if (setStreetAddress) {
        setStreetAddress({ route, number, city, state, zip });
      }

      if (onAfterSelect) {
        onAfterSelect();
      }
    } catch (err) {
      notifyBugsnag(err);
    }
  };

  const clearAddress = () => {
    dispatch(setOrderAddress(null));
    setAddressString('');
    setOnSearch(true);
  };

  return (
    <div>
      <Box className={classes.inputContainer}>
        <span className={classes.addressPin} data-test-id="address-pin">
          {!showIcon && (
            <PersonPinCircleSharpIcon
              color={googleMapsErrorStates ? 'error' : 'primary'}
            />
          )}
        </span>
        <TextField
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          placeholder="Enter delivery address"
          style={{
            borderColor: googleMapsErrorStates ? '#EE1411' : '#D1D1D1',
            width: '100%',
            paddingLeft: '32px',
            height: '50px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
          sx={{
            '& .MuiInputBase-input': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
          className={showIcon ? classes.newAddressInput : classes.addressInput}
          onChange={onInputChange}
          loading={isPlacePredictionsLoading}
          value={addressString}
          data-test-id="autocomplete-search"
          type="text"
          disabled={disabled}
        />
        {addressString && !showIcon && (
          <span
            className={classes.clearAddress}
            onClick={clearAddress}
            data-test-id="clear-address"
            aria-hidden="true"
            disabled={disabled}
          >
            <Close />
          </span>
        )}
      </Box>
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5, ml: 1 }}
        >
          {helperText}
        </Typography>
      )}
      {addressString &&
        addressString.length > debounceSearchMinLength &&
        onSearch &&
        placePredictions !== null &&
        placePredictions.length > 0 &&
        !disabled && (
          <Box>
            <List
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                overflow: 'auto',
                maxHeight: 120,
              }}
            >
              {placePredictions.map((item, index) => {
                if (index > autocompleteMaxResults) {
                  return null;
                }
                return (
                  <Box sx={{ borderRadius: '16px' }} key={item?.place_id}>
                    <ListItemButton
                      data-test-id={`google-autocomplete-item-${item?.place_id}`}
                    >
                      <ListItemText
                        primary={item?.description}
                        primaryTypographyProps={{ fontSize: '14px' }}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'left',
                          alignItems: 'left',
                          textAlign: 'left',
                        }}
                        data-test-id={`google-autocomplete-item-action-${item?.place_id}`}
                        onClick={() => onPlaceSelected(item)}
                      />
                    </ListItemButton>
                  </Box>
                );
              })}
            </List>
          </Box>
        )}
    </div>
  );
}

GoogleAutoCompleteInput.propTypes = {
  setStreetAddress: PropTypes.func,
  showIcon: PropTypes.bool,
  streetAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabled: PropTypes.bool,
  onAfterSelect: PropTypes.func,
  helperText: PropTypes.string,
};

GoogleAutoCompleteInput.defaultProps = {
  setStreetAddress: () => null,
  showIcon: false,
  streetAddress: '',
  disabled: false,
  onAfterSelect: null,
  helperText: null,
};
